import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Edit, User, Phone, Mail, Calendar, Clock, Shield, FileText, Award, Upload, CheckCircle2, AlertTriangle, XCircle, Download, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  attivo: 'bg-green-100 text-green-800',
  bozza: 'bg-slate-100 text-slate-800',
  sospeso: 'bg-yellow-100 text-yellow-800',
  cessato: 'bg-red-100 text-red-800',
  da_completare: 'bg-orange-100 text-orange-800'
};

export default function VolontarioDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [volontario, setVolontario] = useState(null);
  const [ore, setOre] = useState(null);
  const [documenti, setDocumenti] = useState({ documenti: [], tipi_mancanti: [] });
  const [copertura, setCopertura] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [volRes, oreRes, docRes, copRes, checklistRes] = await Promise.all([
        fetch(`${API_URL}/api/volontari/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ore-volontari/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/documenti/volontario/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/coperture?volontario_id=${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/volontari/${id}/checklist-documenti`, { credentials: 'include' })
      ]);

      if (volRes.ok) setVolontario(await volRes.json());
      if (oreRes.ok) setOre(await oreRes.json());
      if (docRes.ok) setDocumenti(await docRes.json());
      if (copRes.ok) {
        const coperture = await copRes.json();
        setCopertura(coperture.find(c => c.stato === 'attiva') || coperture[0] || null);
      }
      if (checklistRes.ok) setChecklist(await checklistRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificaBadge = async () => {
    try {
      const response = await fetch(`${API_URL}/api/badges/verifica/${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.nuovi_badges?.length > 0) {
          toast.success(`Assegnati ${data.nuovi_badges.length} nuovi badge!`);
        } else {
          toast.info('Nessun nuovo badge da assegnare');
        }
        fetchData();
      }
    } catch (error) {
      toast.error('Errore nella verifica badge');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!volontario) {
    return <div>Volontario non trovato</div>;
  }

  return (
    <div className="space-y-6" data-testid="volontario-dettaglio">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/volontari')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {volontario.nome} {volontario.cognome}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statusColors[volontario.stato]}>
                {volontario.stato?.replace('_', ' ').toUpperCase()}
              </Badge>
              <span className="text-slate-500">#{volontario.progressivo}</span>
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/volontari/${id}/modifica`)} data-testid="edit-btn">
          <Edit className="w-4 h-4 mr-2" />
          Modifica
        </Button>
      </div>

      {/* Badges */}
      {ore?.badges?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {ore.badges.map((badge, idx) => (
            <Badge key={idx} className="bg-amber-100 text-amber-800 px-3 py-1">
              <Award className="w-4 h-4 mr-1" />
              {badge.nome}
            </Badge>
          ))}
        </div>
      )}

      <Tabs defaultValue="info" className="w-full">
        <TabsList>
          <TabsTrigger value="info">Informazioni</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="ore">Ore e Presenze</TabsTrigger>
          <TabsTrigger value="documenti">Documenti</TabsTrigger>
          <TabsTrigger value="copertura">Copertura</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-[#0055BB]" />
                  Dati Anagrafici
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><span className="text-slate-500">Codice Fiscale:</span> <span className="font-mono">{volontario.codice_fiscale || '-'}</span></div>
                <div><span className="text-slate-500">Data Nascita:</span> {volontario.data_nascita || '-'}</div>
                <div><span className="text-slate-500">Luogo Nascita:</span> {volontario.luogo_nascita || '-'}</div>
                <div><span className="text-slate-500">Indirizzo:</span> {volontario.indirizzo || '-'}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Phone className="w-5 h-5 text-[#0055BB]" />
                  Contatti
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><span className="text-slate-500">Telefono:</span> {volontario.telefono || '-'}</div>
                <div><span className="text-slate-500">Email:</span> {volontario.email || '-'}</div>
                <div><span className="text-slate-500">PEC:</span> {volontario.pec || '-'}</div>
                <div><span className="text-slate-500">Contatto Emergenza:</span> {volontario.contatto_emergenza || '-'}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-[#0055BB]" />
                  Attività
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><span className="text-slate-500">Data Inizio:</span> {volontario.data_inizio_attivita || '-'}</div>
                <div><span className="text-slate-500">Qualifica:</span> {volontario.qualifica || '-'}</div>
                <div><span className="text-slate-500">Competenze:</span> {volontario.competenze || '-'}</div>
                <div><span className="text-slate-500">Disponibilità:</span> {volontario.disponibilita || '-'}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-[#0055BB]" />
                  Riepilogo Ore
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-[#0055BB]/10 rounded-lg">
                    <p className="text-3xl font-bold text-[#0055BB]">{ore?.ore_totali || 0}</p>
                    <p className="text-sm text-slate-500">Ore Totali</p>
                  </div>
                  <div className="text-center p-4 bg-[#008A55]/10 rounded-lg">
                    <p className="text-3xl font-bold text-[#008A55]">{ore?.ore_anno_corrente || 0}</p>
                    <p className="text-sm text-slate-500">Ore Anno</p>
                  </div>
                </div>
                <Button onClick={handleVerificaBadge} variant="outline" className="w-full mt-4">
                  <Award className="w-4 h-4 mr-2" />
                  Verifica Badge
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-[#0055BB]" />
                    Checklist Documenti Compliance
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Stato dei documenti obbligatori per il volontario
                  </p>
                </div>
                {checklist?.riepilogo && (
                  <div className="text-right">
                    <p className="text-3xl font-bold text-[#0055BB]">
                      {checklist.riepilogo.percentuale_completamento}%
                    </p>
                    <p className="text-xs text-slate-500">Completamento</p>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {checklist?.riepilogo && (
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-700">{checklist.riepilogo.totale_obbligatori}</p>
                    <p className="text-xs text-slate-500">Richiesti</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xl font-bold text-green-600">{checklist.riepilogo.completi}</p>
                    <p className="text-xs text-green-600">Completi</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-xl font-bold text-amber-600">{checklist.riepilogo.da_aggiornare}</p>
                    <p className="text-xs text-amber-600">Da aggiornare</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-xl font-bold text-red-600">{checklist.riepilogo.mancanti}</p>
                    <p className="text-xs text-red-600">Mancanti</p>
                  </div>
                </div>
              )}
              
              {checklist?.checklist?.length > 0 ? (
                <div className="space-y-3">
                  {checklist.checklist.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border ${
                        item.stato === 'valido' ? 'bg-green-50 border-green-200' :
                        item.stato === 'da_aggiornare' ? 'bg-amber-50 border-amber-200' :
                        'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {item.stato === 'valido' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : item.stato === 'da_aggiornare' ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{item.nome}</p>
                            {item.obbligatorio && (
                              <Badge variant="outline" className="text-xs">Obbligatorio</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {item.presente ? (
                            <div className="text-sm">
                              <p className="text-slate-500">v{item.documento?.versione}</p>
                              {item.documento?.data_scadenza && (
                                <p className="text-xs text-slate-400">Scade: {item.documento.data_scadenza}</p>
                              )}
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => navigate(`/documenti?volontario=${id}&categoria=${item.categoria}`)}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Carica
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">
                  Caricamento checklist documenti...
                </p>
              )}
              
              <div className="mt-6 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/kit-amministrativo')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Genera Documenti dal Kit Amministrativo
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ore" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Storico Presenze</CardTitle>
            </CardHeader>
            <CardContent>
              {ore?.presenze?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Evento</TableHead>
                      <TableHead>Check-in</TableHead>
                      <TableHead>Check-out</TableHead>
                      <TableHead>Ore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ore.presenze.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{p.data || '-'}</TableCell>
                        <TableCell>{p.evento_titolo || '-'}</TableCell>
                        <TableCell className="text-green-600 font-mono">{p.ora_checkin || '-'}</TableCell>
                        <TableCell className="text-red-600 font-mono">{p.ora_checkout || '-'}</TableCell>
                        <TableCell>{p.ore_calcolate?.toFixed(1) || '-'} h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-slate-500 py-8">Nessuna presenza registrata</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documenti" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0055BB]" />
                Documenti Volontario
              </CardTitle>
              <Button onClick={() => navigate(`/documenti?volontario=${id}`)}>
                <Upload className="w-4 h-4 mr-2" />
                Carica Documento
              </Button>
            </CardHeader>
            <CardContent>
              {documenti.tipi_mancanti?.length > 0 && (
                <div className="alert-warning mb-4">
                  <p className="font-medium">Documenti mancanti:</p>
                  <ul className="text-sm mt-1">
                    {documenti.tipi_mancanti.map((tipo, idx) => (
                      <li key={idx}>• {tipo.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                    ))}
                  </ul>
                </div>
              )}
              {documenti.documenti?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome File</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Versione</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documenti.documenti.map((doc, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{doc.nome_file}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.categoria}</Badge>
                        </TableCell>
                        <TableCell>{new Date(doc.created_at).toLocaleDateString('it-IT')}</TableCell>
                        <TableCell>v{doc.versione}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-slate-500 py-8">Nessun documento caricato</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="copertura" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0055BB]" />
                Copertura Assicurativa
              </CardTitle>
            </CardHeader>
            <CardContent>
              {copertura ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Stato:</span>
                    <Badge className={copertura.stato === 'attiva' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {copertura.stato?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><span className="text-slate-500">Compagnia:</span> {copertura.compagnia || '-'}</div>
                    <div><span className="text-slate-500">N. Polizza:</span> <span className="font-mono">{copertura.numero_polizza || '-'}</span></div>
                    <div><span className="text-slate-500">Decorrenza:</span> {copertura.decorrenza || '-'}</div>
                    <div><span className="text-slate-500">Scadenza:</span> {copertura.scadenza || '-'}</div>
                    <div><span className="text-slate-500">Tipologia:</span> {copertura.tipologia || '-'}</div>
                  </div>
                </div>
              ) : (
                <div className="alert-warning">
                  <p className="font-medium">Nessuna copertura assicurativa attiva</p>
                  <p className="text-sm mt-1">Aggiungi una copertura dalla sezione Coperture.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
